import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    MenuItem,
    Select,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { NotFound } from '../layout/SystemState';
import { getAssignment, getRun, saveAssignment, startRun } from '../../services/runService';
import { BackToRuns, Card, WizardSteps, fmt } from './parts';

const COLUMNS = [
    { key: 'a', label: 'Adjudicator 1' },
    { key: 'b', label: 'Adjudicator 2' },
    { key: 't', label: 'Tie-breaker' },
];

const rowFrom = (lesson) => ({ a: lesson.blindSlotIds?.[0] || '', b: lesson.blindSlotIds?.[1] || '', t: lesson.tieBreakSlotId || '' });

// Why a row cannot be saved (wf-run-assign-9), or null. An untouched row is unassigned, not wrong.
const problemOf = ({ a, b, t }) => {
    if (!a && !b && !t) return null;
    if (a && b && a === b) return { field: 'b', text: 'Same slot twice' };
    if (t && (t === a || t === b)) return { field: 't', text: 'Tie-breaker codes it' };
    if (!a || !b) return { field: !a ? 'a' : 'b', text: 'Needs two adjudicators' };
    if (!t) return { field: 't', text: 'No tie-breaker' };
    return null;
};

const complete = (row) => Boolean(row.a && row.b && row.t) && !problemOf(row);

// Load per slot, from complete rows only: queued items it codes blind, lessons it breaks ties for,
// and how many lessons each pair codes together
const loadOf = (slots, lessons, rows) => {
    const bySlot = Object.fromEntries(slots.map((s) => [s.id, { items: 0, ties: 0 }]));
    const pairs = {};
    lessons.forEach((lesson) => {
        const row = rows[lesson.lessonId];
        if (!complete(row)) return;
        [row.a, row.b].forEach((id) => { if (bySlot[id]) bySlot[id].items += lesson.items; });
        if (bySlot[row.t]) bySlot[row.t].ties += 1;
        const key = [row.a, row.b].sort().join('|');
        pairs[key] = (pairs[key] || 0) + 1;
    });
    return { bySlot, pairs };
};

const SlotSelect = ({ value, slots, onChange, label, problem, disabled }) => (
    <FormControl size="small" sx={{ minWidth: 104 }} error={Boolean(problem)}>
        <Select
            displayEmpty
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            inputProps={{ 'aria-label': label }}
            renderValue={(id) => (id ? slots.find((s) => s.id === id)?.name : <Box component="span" sx={{ color: 'warning.dark' }}>Choose</Box>)}
            sx={{ '& fieldset': !value && !problem ? { borderColor: 'warning.main' } : {} }}
        >
            {slots.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}{s.account ? ` · ${s.account}` : ''}</MenuItem>)}
        </Select>
    </FormControl>
);

// /runs/:runId/setup (wf-run-assign 8–9, YES-259): each lesson gets two adjudicators and a third
// slot as its tie-breaker, chosen by the researcher with the load in view; ADAS assigns nothing by
// itself. Start opens the run once every lesson is assigned; lessons already being coded are fixed.
const AssignLessons = () => {
    const { runId } = useParams();
    const navigate = useNavigate();
    const [run, setRun] = useState(null);
    const [data, setData] = useState(null);
    const [rows, setRows] = useState({});
    const [saved, setSaved] = useState({});
    const [status, setStatus] = useState('loading');
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    const load = useCallback(async () => {
        setStatus('loading');
        try {
            const [r, a] = await Promise.all([getRun(runId), getAssignment(runId)]);
            setRun(r);
            setData(a);
            const fresh = Object.fromEntries(a.lessons.map((l) => [l.lessonId, rowFrom(l)]));
            setRows(fresh);
            setSaved(fresh);
            setStatus('ready');
        } catch (err) {
            setStatus(err.response?.status === 404 ? 'notFound' : 'error');
        }
    }, [runId]);

    useEffect(() => {
        load();
    }, [load]);

    const lessons = useMemo(() => data?.lessons || [], [data]);
    const slots = useMemo(() => data?.slots || [], [data]);
    const problems = lessons.map((l) => ({ lesson: l, problem: problemOf(rows[l.lessonId] || {}) })).filter((p) => p.problem);
    const assigned = lessons.filter((l) => complete(rows[l.lessonId] || {})).length;
    const dirty = lessons.some((l) => JSON.stringify(rows[l.lessonId]) !== JSON.stringify(saved[l.lessonId]));
    const { bySlot, pairs } = useMemo(() => loadOf(slots, lessons, rows), [slots, lessons, rows]);
    const isOpen = run?.status === 'open';

    const setCell = (lessonId, key, value) => {
        setNotice(null);
        setRows((r) => ({ ...r, [lessonId]: { ...r[lessonId], [key]: value } }));
    };

    const save = async () => {
        setBusy('save');
        setError(null);
        try {
            const changed = lessons
                .filter((l) => !l.locked && complete(rows[l.lessonId]) && JSON.stringify(rows[l.lessonId]) !== JSON.stringify(saved[l.lessonId]))
                .map((l) => ({ lessonId: l.lessonId, blindSlotIds: [rows[l.lessonId].a, rows[l.lessonId].b], tieBreakSlotId: rows[l.lessonId].t }));
            const next = await saveAssignment(runId, changed);
            setData(next);
            const fresh = Object.fromEntries(next.lessons.map((l) => [l.lessonId, rowFrom(l)]));
            setRows((r) => ({ ...r, ...fresh }));
            setSaved(fresh);
            setNotice(`Saved ${changed.length} ${changed.length === 1 ? 'lesson' : 'lessons'}.`);
            return true;
        } catch (err) {
            setError(err.response?.status === 409
                ? 'A lesson’s items are already being coded, so its assignment is fixed. Reload to see it.'
                : err.response?.data?.error || 'Couldn’t save. Nothing was saved. Try again.');
            return false;
        } finally {
            setBusy(null);
        }
    };

    const start = async () => {
        if (dirty && !(await save())) return;
        setBusy('start');
        setError(null);
        try {
            await startRun(runId);
            navigate(`/runs/${encodeURIComponent(runId)}`, { replace: true });
        } catch (err) {
            setBusy(null);
            setError(err.response?.status === 409 ? 'Every lesson needs two adjudicators and a tie-breaker before the run starts.' : 'Couldn’t start the run. Try again.');
        }
    };

    if (status === 'notFound') return <NotFound />;
    if (status === 'error') {
        return (
            <Box sx={{ maxWidth: 600, mx: 'auto', mt: 6 }}>
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>Couldn’t load the run’s lessons.</Alert>
            </Box>
        );
    }

    const startBlock = problems.length
        ? `Fix the ${problems.length} ${problems.length === 1 ? 'lesson' : 'lessons'} first`
        : assigned < lessons.length ? `Assign all ${lessons.length} lessons first` : null;

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            <BackToRuns />
            <Typography variant="h2" component="h1" sx={{ mt: 0.5 }}>
                {run ? (isOpen ? `Edit assignment · ${run.name}` : `Set up ${run.name}`) : <Skeleton width={320} />}
            </Typography>
            {!isOpen && <WizardSteps active={3} />}
            {isOpen && <Box sx={{ mb: 3 }} />}

            <Grid container spacing={2.5} alignItems="flex-start">
                <Grid item xs={12} lg={8}>
                    <Card title="Lessons">
                        <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5, mb: 2 }}>
                            Give each lesson two adjudicators and a third as its tie-breaker. Only the lesson’s flagged and control items are queued.
                        </Typography>
                        {status === 'loading' ? [0, 1, 2, 3].map((i) => <Skeleton key={i} height={44} />) : (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small" aria-label="Lesson assignment" sx={{ minWidth: 560 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Lesson</TableCell>
                                            <TableCell>Items</TableCell>
                                            {COLUMNS.map((c) => <TableCell key={c.key}>{c.label}</TableCell>)}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {lessons.map((lesson) => {
                                            const row = rows[lesson.lessonId] || { a: '', b: '', t: '' };
                                            const problem = problemOf(row);
                                            return (
                                                <TableRow key={lesson.lessonId} sx={{ bgcolor: problem ? (theme) => alpha(theme.palette.error.main, 0.04) : 'transparent' }}>
                                                    <TableCell sx={{ fontWeight: 700 }}>{lesson.name}</TableCell>
                                                    <TableCell>{fmt(lesson.items)}</TableCell>
                                                    {COLUMNS.map((c) => (
                                                        <TableCell key={c.key}>
                                                            <SlotSelect
                                                                value={row[c.key]}
                                                                slots={slots}
                                                                label={`${c.label} for ${lesson.name}`}
                                                                problem={problem?.field === c.key}
                                                                disabled={lesson.locked}
                                                                onChange={(v) => setCell(lesson.lessonId, c.key, v)}
                                                            />
                                                            {problem?.field === c.key && (
                                                                <Typography variant="caption" component="p" color="error.dark" sx={{ mt: 0.5 }}>{problem.text}</Typography>
                                                            )}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}

                        {status === 'ready' && (
                            <Alert severity={problems.length ? 'error' : assigned === lessons.length ? 'success' : 'warning'} icon={false} sx={{ mt: 2 }}>
                                {problems.length
                                    ? `${problems.length} ${problems.length === 1 ? 'lesson needs' : 'lessons need'} a change: ${problems.map((p) => `${p.lesson.name} (${p.problem.text.toLowerCase()})`).join(', ')}.`
                                    : `${assigned} of ${lessons.length} lessons assigned. The run starts once every lesson has two adjudicators and a tie-breaker.`}
                            </Alert>
                        )}
                        {lessons.some((l) => l.locked) && (
                            <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1 }}>
                                Lessons whose items are already being coded are fixed.
                            </Typography>
                        )}
                        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                        {notice && !error && <Alert severity="success" sx={{ mt: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                            <Button
                                variant="outlined"
                                onClick={save}
                                disabled={!dirty || Boolean(busy) || problems.length > 0}
                                startIcon={busy === 'save' ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                Save
                            </Button>
                            {!isOpen && (
                                <Box sx={{ textAlign: 'right' }}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={start}
                                        disabled={Boolean(startBlock) || Boolean(busy)}
                                        startIcon={busy === 'start' ? <CircularProgress size={16} color="inherit" /> : null}
                                    >
                                        Start the run
                                    </Button>
                                    {startBlock && <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 0.75 }}>{startBlock}</Typography>}
                                </Box>
                            )}
                        </Box>
                    </Card>
                </Grid>

                <Grid item xs={12} lg={4}>
                    <Card title="Load">
                        <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5, mb: 1.5 }}>
                            {problems.length ? 'Counts leave out the lessons that cannot be saved.' : 'Updates as you pick; all lessons of the run.'}
                        </Typography>
                        <Table size="small" aria-label="Load per slot">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Slot</TableCell>
                                    <TableCell>Account</TableCell>
                                    <TableCell align="right">Items</TableCell>
                                    <TableCell align="right">Tie-breaker</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {slots.map((s) => (
                                    <TableRow key={s.id}>
                                        <TableCell sx={{ fontWeight: 700 }}>{s.name}</TableCell>
                                        <TableCell sx={{ color: 'text.secondary', overflowWrap: 'anywhere' }}>{s.account ? s.account.split('@')[0] : '—'}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(bySlot[s.id]?.items || 0)}</TableCell>
                                        <TableCell align="right">{bySlot[s.id]?.ties || 0} {bySlot[s.id]?.ties === 1 ? 'lesson' : 'lessons'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <Typography variant="overline" component="h3" color="text.secondary" sx={{ display: 'block', mt: 2.5, mb: 1 }}>Pairs · lessons coded together</Typography>
                        <Box role="table" aria-label="Lessons each pair codes together" sx={{ display: 'grid', gridTemplateColumns: `32px repeat(${slots.length}, 1fr)`, gap: 0.5 }}>
                            <span />
                            {slots.map((s) => <Typography key={s.id} role="columnheader" variant="caption" align="center" sx={{ fontWeight: 600 }}>{s.name}</Typography>)}
                            {slots.map((row, i) => (
                                <React.Fragment key={row.id}>
                                    <Typography role="rowheader" variant="caption" sx={{ fontWeight: 600, alignSelf: 'center' }}>{row.name}</Typography>
                                    {slots.map((col, j) => {
                                        const n = j > i ? pairs[[row.id, col.id].sort().join('|')] || 0 : null;
                                        return (
                                            <Box key={col.id} role="cell" sx={{ height: 26, borderRadius: 1, display: 'grid', placeItems: 'center', fontSize: '0.75rem', fontWeight: 600, bgcolor: n ? (theme) => alpha(theme.palette.primary.main, 0.1) : 'action.hover', color: 'primary.main' }}>
                                                {n || ''}
                                            </Box>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </Box>
                        <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1.5 }}>
                            Items: queued items each slot codes blind. Tie-breaker: lessons whose ties go to the slot.
                        </Typography>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AssignLessons;
